import React from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useScrollToSection } from "~/single-page/hooks/useScrollToSection";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PeopleIcon from '@mui/icons-material/People';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

const scrollMarginStyle = {
  scrollMarginTop: '100px'
};

export default function OrganDonorPage() {
  useScrollToSection();

  return (
    <Stack spacing={6} sx={{ maxWidth: '100%' }}>
      {/* Header - Atualizado com texto oficial */}
      <Box id="about-donation" sx={scrollMarginStyle}>
      <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold', 
          mb: 3,
          mt: 2,
          color: '#1B4B72',
          textAlign: 'center'
        }}>
          Doação de Órgãos
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '800px', 
          mx: 'auto',
          mb: 3
        }}>
          A doação de órgãos é um ato por meio do qual podem ser retirados órgãos ou tecidos 
          de uma pessoa viva ou falecida (doadores) para serem utilizados no tratamento de outras 
          pessoas (receptores), com a finalidade de reestabelecer as funções de um órgão ou tecido doente.
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '800px', 
          mx: 'auto'
        }}>
          Na maioria das vezes, o transplante de órgãos pode ser a única esperança de vida ou 
          a oportunidade de um recomeço para as pessoas que precisam da doação.
        </Typography>
      </Box>

      {/* Estatísticas - Dados oficiais do Brasil */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 3,
        px: 2
      }}>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <LocalHospitalIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#009FE3', fontWeight: 'bold', mb: 1 }}>
              2º Lugar
            </Typography>
            <Typography variant="body1">
              Maior programa público de transplantes do mundo, atrás apenas dos EUA
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PeopleIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#009FE3', fontWeight: 'bold', mb: 1 }}>
              Referência
            </Typography>
            <Typography variant="body1">
              O Brasil é referência mundial na área de transplantes
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <MedicalServicesIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#009FE3', fontWeight: 'bold', mb: 1 }}>
              100% SUS
            </Typography>
            <Typography variant="body1">
              Assistência integral e medicamentos pós-transplante gratuitos
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Como funciona - Explicação */}
      <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold', 
          mb: 4,
          color: '#1B4B72'
        }}>
          Como funciona a doação?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          No Brasil, a doação de órgãos e tecidos só é realizada após autorização familiar. 
          Mesmo que você tenha manifestado em vida o desejo de ser doador, a doação só acontece 
          se sua família autorizar.
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Por isso, é fundamental conversar com sua família e deixar claro seu desejo de ser doador. 
          Na maioria das vezes, os familiares respeitam essa vontade quando ela é comunicada em vida.
        </Typography>
      </Box>

      {/* Tipos de Doação */}
      <Box id="donation-types" sx={scrollMarginStyle}>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold', 
          mb: 4, 
          textAlign: 'center',
          color: '#1B4B72'
        }}>
          Tipos de Doação
        </Typography>
        
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 4
        }}>
          {/* Doação em Vida */}
          <Card sx={{ borderRadius: '24px' }}>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 3,
                borderBottom: '1px solid #E0E0E0'
              }}>
                <VolunteerActivismIcon sx={{ 
                  fontSize: 40,
                  color: '#009FE3'
                }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: '#1B4B72' }}>
                  Doação em Vida
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  É possível doar em vida:
                  <ul>
                    <li>Um dos rins</li>
                    <li>Parte do fígado</li>
                    <li>Parte dos pulmões</li>
                    <li>Medula óssea</li>
                  </ul>
                </Typography>
                <Typography variant="body1">
                  Para ser doador em vida é necessário:
                  <ul>
                    <li>Ser maior de idade e juridicamente capaz</li>
                    <li>Estar em boas condições de saúde</li>
                    <li>Ter compatibilidade sanguínea com o receptor</li>
                    <li>Passar por avaliação médica completa</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* Doação Post Mortem */}
          <Card sx={{ borderRadius: '24px' }}>
            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 3,
                borderBottom: '1px solid #E0E0E0'
              }}>
                <FavoriteIcon sx={{ 
                  fontSize: 40,
                  color: '#009FE3'
                }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: '#1B4B72' }}>
                  Doação Post Mortem
                </Typography>
              </Box>
              <Box sx={{ p: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Pode ocorrer em duas situações:
                  <ul>
                    <li>Após diagnóstico de morte encefálica</li>
                    <li>Após parada cardiorrespiratória</li>
                  </ul>
                </Typography>
                <Typography variant="body1">
                  Órgãos que podem ser doados e tempo máximo para transplante:
                  <ul>
                    <li>Coração (4 horas)</li>
                    <li>Pulmões (4-6 horas)</li>
                    <li>Rins (48 horas)</li>
                    <li>Fígado (12 horas)</li>
                    <li>Pâncreas (12 horas)</li>
                    <li>Intestino (4-6 horas)</li>
                  </ul>
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* Call to Action - Com AEDO */}
      <Box id="become-donor" sx={scrollMarginStyle}>
        <Card sx={{ 
          borderRadius: '24px',
          border: '2px solid #009FE3'
        }}>
          <CardContent sx={{ px: 4, py: 6, textAlign: 'center' }}>
            <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', mb: 3, color: '#1B4B72' }}>
              Quero ser doador
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
              A melhor maneira de garantir que sua vontade seja respeitada é conversando com sua família. 
              Além disso, você pode registrar seu desejo através da AEDO (Autorização Eletrônica de Doação de Órgãos), 
              um sistema online gratuito e seguro.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Typography
                component="a"
                href="https://www.aedo.org.br"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'inline-block',
                  border: '2px solid #009FE3',
                  color: '#009FE3',
                  textDecoration: 'none',
                  px: 4,
                  py: 2,
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#F8F9FA' }
                }}
              >
                Registrar na AEDO →
              </Typography>
              <Typography
                component="a"
                href="tel:136"
                sx={{ 
                  display: 'inline-block',
                  border: '2px solid #009FE3',
                  color: '#009FE3',
                  textDecoration: 'none',
                  px: 4,
                  py: 2,
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#F8F9FA' }
                }}
              >
                Dúvidas? Ligue 136
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 3, color: '#666' }}>
              A AEDO é um sistema online que permite registrar seu desejo de ser doador de órgãos 
              de forma segura e gratuita.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
